import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Skeleton } from '@/components/ui/skeleton'
import { ActionPopover } from '@/components/action-popover'
import { POInlineEditableField } from '@/components/po-inline-edit'
import {
  correctionDetail,
  correctionErrorMessage,
  useProductCorrections,
  useSaveProductCorrections,
} from '@/api/product-corrections'

type Overrides = Record<string, string | null>

const REASON_NOTE = { label: 'Reason', placeholder: 'Why? (optional)', singleLine: true }

/** Who changed a value, when and why, and the value it replaced; Undo inside. */
export function CorrectionPopover({
  name,
  label,
  detail,
  was,
  pending,
  onUndo,
}: {
  name: string
  label: string
  detail: string
  was: string | null | undefined
  pending: boolean
  onUndo: (close: () => void) => void
}) {
  return (
    <ActionPopover
      align="end"
      title={name}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs font-normal text-primary"
          aria-label={`${label}: ${name}`}
        >
          {label}
        </Button>
      }
      confirmLabel="Undo"
      confirmVariant="outline"
      pending={pending}
      pendingLabel="Undoing..."
      onConfirm={(_note, close) => onUndo(close)}
    >
      <p className="text-xs text-muted-foreground">{detail}</p>
      {was != null && <p className="text-sm">Was {was}</p>}
    </ActionPopover>
  )
}

/** The spec table the store shows: staff can change a value, remove a row or add one. */
export function ProductSpecTable({ productId }: { productId: string }) {
  const { data, isLoading, isError, refetch } = useProductCorrections(productId)
  const save = useSaveProductCorrections(productId)
  const [busyRow, setBusyRow] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newValue, setNewValue] = useState('')

  const overrides: Overrides = data?.spec_overrides?.value ?? {}
  const aiValues = new Map((data?.ai_spec_table ?? []).map((row) => [row.name, row.value]))
  const detail = data?.spec_overrides
    ? correctionDetail(data.spec_overrides.by, data.spec_overrides.at, data.spec_overrides.reason)
    : ''
  const removed = Object.entries(overrides)
    .filter(([, value]) => value == null)
    .map(([name]) => name)

  /** Saves the whole staff map; an empty map undoes the spec table correction. */
  const saveOverrides = (
    row: string,
    next: Overrides,
    reason: string | null,
    onSuccess?: () => void,
  ) => {
    setBusyRow(row)
    save.mutate(
      Object.keys(next).length === 0
        ? { release: ['spec_overrides'] }
        : { spec_overrides: next, reason },
      {
        onSuccess,
        onError: (error) =>
          toast.error(correctionErrorMessage(error, 'Could not save the spec table')),
        onSettled: () => setBusyRow(null),
      },
    )
  }
  const without = (name: string) => {
    const next = { ...overrides }
    delete next[name]
    return next
  }
  const busy = (name: string) => save.isPending && busyRow === name

  const setValue = (name: string, raw: string) => {
    const value = raw.trim()
    if (!value) return
    // Typing the original value back hands the row back to the AI.
    const next = aiValues.get(name) === value ? without(name) : { ...overrides, [name]: value }
    saveOverrides(name, next, null)
  }
  const removeRow = (name: string, reason: string, close: () => void) =>
    // A row staff added goes away; an AI row stays removed until undone.
    saveOverrides(
      name,
      aiValues.has(name) ? { ...overrides, [name]: null } : without(name),
      reason || null,
      close,
    )
  const addRow = (reason: string, close: () => void) =>
    saveOverrides(
      newName.trim(),
      { ...overrides, [newName.trim()]: newValue.trim() },
      reason || null,
      () => {
        setNewName('')
        setNewValue('')
        close()
      },
    )

  return (
    <section aria-labelledby="product-spec-table" className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2">
        <h2 id="product-spec-table" className="text-sm font-semibold">
          Spec table
        </h2>
        {data && (
          <ActionPopover
            align="end"
            title="Add a row"
            trigger={
              <Button variant="ghost" size="sm" className="h-8">
                <Plus className="h-4 w-4" />
                Add row
              </Button>
            }
            note={REASON_NOTE}
            confirmLabel="Add row"
            canConfirm={!!newName.trim() && !!newValue.trim()}
            pending={busy(newName.trim())}
            pendingLabel="Saving..."
            onConfirm={(reason, close) => addRow(reason, close)}
          >
            <InlineFieldInput
              aria-label="Name"
              placeholder="Name, e.g. Interior Finish"
              value={newName}
              maxLength={120}
              onChange={(event) => setNewName(event.target.value)}
            />
            <InlineFieldInput
              aria-label="Value"
              placeholder="Value"
              value={newValue}
              maxLength={500}
              onChange={(event) => setNewValue(event.target.value)}
            />
          </ActionPopover>
        )}
      </div>
      {isLoading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-6" />
          <Skeleton className="h-6" />
        </div>
      ) : isError || !data ? (
        <div className="flex items-center gap-3 px-4 py-3 text-sm">
          <span className="text-destructive">Could not load the spec table.</span>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : data.spec_table.length === 0 && removed.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">No spec table yet.</p>
      ) : (
        <ul className="divide-y">
          {data.spec_table.map((row) => {
            const corrected = overrides[row.name] != null
            return (
              <li
                key={row.name}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 px-4 py-1 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]"
              >
                <span className="min-w-0 truncate pt-1 text-sm text-muted-foreground sm:pt-0">
                  {row.name}
                </span>
                <div className="col-start-1 row-start-2 min-w-0 sm:col-start-auto sm:row-start-auto">
                  <POInlineEditableField
                    value={row.value}
                    ariaLabel={`Edit ${row.name}`}
                    title={`Edit ${row.name}`}
                    pending={busy(row.name)}
                    buttonClassName="-ml-2 font-medium"
                    showEditIcon
                    onConfirm={(raw) => setValue(row.name, raw)}
                  />
                </div>
                <div className="col-start-2 row-span-2 row-start-1 flex items-center justify-end gap-0.5 sm:col-start-auto sm:row-span-1 sm:row-start-auto">
                  {corrected && (
                    <CorrectionPopover
                      name={row.name}
                      label="Corrected"
                      detail={detail}
                      was={aiValues.get(row.name)}
                      pending={busy(row.name)}
                      onUndo={(close) => saveOverrides(row.name, without(row.name), null, close)}
                    />
                  )}
                  <ActionPopover
                    align="end"
                    title={`Remove ${row.name}`}
                    trigger={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-muted-foreground"
                        aria-label={`Remove ${row.name}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    }
                    note={REASON_NOTE}
                    confirmLabel="Remove"
                    pending={busy(row.name)}
                    pendingLabel="Removing..."
                    onConfirm={(reason, close) => removeRow(row.name, reason, close)}
                  />
                </div>
              </li>
            )
          })}
          {removed.map((name) => (
            <li
              key={name}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 px-4 py-1 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]"
            >
              <span className="min-w-0 truncate pt-1 text-sm text-muted-foreground line-through sm:pt-0">
                {name}
              </span>
              <span className="col-start-1 row-start-2 min-h-8 min-w-0 truncate py-1.5 text-sm text-muted-foreground line-through sm:col-start-auto sm:row-start-auto">
                {aiValues.get(name) ?? ''}
              </span>
              <div className="col-start-2 row-span-2 row-start-1 flex items-center justify-end sm:col-start-auto sm:row-span-1 sm:row-start-auto">
                <CorrectionPopover
                  name={name}
                  label="Removed"
                  detail={detail}
                  was={null}
                  pending={busy(name)}
                  onUndo={(close) => saveOverrides(name, without(name), null, close)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
